import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/solid';

// Mismos tokens que HomeBoldSunset.jsx / Navbar.jsx / About.jsx / Projects.jsx / Contact.jsx
const COLORS = {
  bg: '#200A08',
  surface: '#341210',
  surfaceAlt: '#411713',
  orange: '#FF7A33',
  gold: '#FFC93C',
  teal: '#2EC4B6',
  ink: '#FBEDE2',
  muted: '#C9A79B',
};

const FONT_DISPLAY = '"Arial Black", "Helvetica Neue", Arial, sans-serif';
const FONT_BODY = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_MONO = 'ui-monospace, "Cascadia Code", "SFMono-Regular", Menlo, monospace';

const SectionTitle = ({ children }) => (
  <h3
    style={{ fontFamily: FONT_MONO, color: COLORS.teal, borderBottom: `1px solid ${COLORS.surfaceAlt}`, letterSpacing: '0.1em' }}
    className="text-sm uppercase pb-2 mb-4"
  >
    {children}
  </h3>
);

function CvWeb() {
  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, fontFamily: FONT_BODY }} className="min-h-screen w-full py-8 px-4 sm:px-6 lg:px-8">
      <div style={{ background: COLORS.surface, borderRadius: 14 }} className="max-w-4xl mx-auto overflow-hidden">
        {/* Header */}
        <div className="relative p-6 md:p-8 overflow-hidden" style={{ background: COLORS.surfaceAlt }}>
          <span
            style={{ background: COLORS.orange, transform: 'skewY(-2deg)' }}
            className="absolute inset-x-0 top-0 h-2"
          />
          <h1 style={{ fontFamily: FONT_DISPLAY, letterSpacing: '-0.01em' }} className="text-2xl md:text-3xl uppercase">
            Julio Alberto Muñoz García
          </h1>
          <h2 style={{ color: COLORS.gold, fontFamily: FONT_MONO, letterSpacing: '0.08em' }} className="text-base md:text-lg mt-1 uppercase">
            Full Stack Developer
          </h2>
          <div style={{ color: COLORS.muted }} className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            <div className="flex items-center">
              <PhoneIcon className="h-5 w-5 mr-2" style={{ color: COLORS.teal }} />
              <span>+52 4682393765</span>
            </div>
            <div className="flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" style={{ color: COLORS.teal }} />
              <span>Guanajuato, México</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 md:p-8 grid md:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="md:col-span-2 space-y-8">
            {/* Professional Profile */}
            <section>
              <SectionTitle>Perfil profesional</SectionTitle>
              <p style={{ color: COLORS.muted }}>
                Desarrollador Full Stack con enfoque analítico y actitud resolutiva. Me especializo en Laravel, React
                y MySQL, integrando soluciones técnicas con visión estratégica. Me motiva trabajar en equipo,
                automatizar procesos y generar mejoras sostenibles en los sistemas que desarrollo.
              </p>
            </section>

            {/* Experience */}
            <section>
              <SectionTitle>Experiencia</SectionTitle>

              <div className="mb-6">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <h4 style={{ fontFamily: FONT_DISPLAY }} className="text-base md:text-lg">
                    FullStack Developer
                  </h4>
                  <span style={{ background: COLORS.surfaceAlt, color: COLORS.gold, fontFamily: FONT_MONO }} className="text-xs px-2 py-1 rounded">
                    09/23 - 04/25
                  </span>
                </div>
                <p style={{ color: COLORS.orange }} className="font-medium">Envasadora Aguida - Guanajuato</p>
                <ul style={{ color: COLORS.muted }} className="mt-2 list-disc list-inside space-y-1">
                  <li>Desarrollé e implementé soluciones para el control de versiones, monitoreo y análisis de procesos de envasado, reduciendo el uso de papel en un 80% y mejorando la velocidad de consultas en un 20%.</li>
                  <li>Optimicé estructuras de datos con JSON, automatizando aplicaciones al inicio del sistema.</li>
                  <li>Desarrollé interfaces conectadas a bases de datos, incluyendo un sistema para el área de sanitizantes que mejoró precisión y redujo errores operativos.</li>
                </ul>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <h4 style={{ fontFamily: FONT_DISPLAY }} className="text-base md:text-lg">
                    FullStack Developer
                  </h4>
                  <span style={{ background: COLORS.surfaceAlt, color: COLORS.gold, fontFamily: FONT_MONO }} className="text-xs px-2 py-1 rounded">
                    01/23 - 09/23
                  </span>
                </div>
                <p style={{ color: COLORS.orange }} className="font-medium">Almacenadora de Occidente Xtrapak Jalisco</p>
                <ul style={{ color: COLORS.muted }} className="mt-2 list-disc list-inside space-y-1">
                  <li>Desarrollé e implementé funciones clave en el sistema de control de acceso, incluyendo una API de WhatsApp para alertas y un módulo de geolocalización.</li>
                  <li>Diseñé un Help Desk para soporte técnico y optimicé la gestión de tickets, eliminando el uso de papel mediante su digitalización.</li>
                </ul>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <h4 style={{ fontFamily: FONT_DISPLAY }} className="text-base md:text-lg">
                    FullStack Developer
                  </h4>
                  <span style={{ background: COLORS.surfaceAlt, color: COLORS.gold, fontFamily: FONT_MONO }} className="text-xs px-2 py-1 rounded">
                    06/22 - 12/22
                  </span>
                </div>
                <p style={{ color: COLORS.orange }} className="font-medium">Lerco Puebla</p>
                <ul style={{ color: COLORS.muted }} className="mt-2 list-disc list-inside space-y-1">
                  <li>Colaboré en el desarrollo y mantenimiento de sistemas internos con PHP y Yii2, atendiendo requerimientos funcionales.</li>
                  <li>Implementé mejoras en módulos como Cobro Cajas y Registro de Padres, personalizando formularios, notificaciones y tickets.</li>
                  <li>Participé en la configuración y pruebas de entornos locales para el despliegue de aplicaciones.</li>
                </ul>
              </div>
            </section>

            {/* Key Achievements */}
            <section>
              <SectionTitle>Logros clave</SectionTitle>
              <ul style={{ color: COLORS.muted }} className="list-disc list-inside space-y-2">
                <li>Desarrollé e implementé aplicaciones para el control de versiones en procesos de envasado, mejorando la trazabilidad y reduciendo errores.</li>
                <li>Optimicé la generación de fórmulas en el área de preparación, aumentando en un 20% la velocidad de procesamiento y reduciendo en un 30% el espacio de almacenamiento por producción.</li>
                <li>Estandaricé la nomenclatura de materias primas para agilizar las consultas en los módulos del sistema de producción y monitoreo.</li>
              </ul>
            </section>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Education */}
            <section>
              <SectionTitle>Educación</SectionTitle>
              <div>
                <h4 style={{ fontFamily: FONT_DISPLAY }} className="text-sm md:text-base">
                  Ingeniería en Sistemas Computacionales — 2015
                </h4>
                <p style={{ color: COLORS.muted }} className="text-sm mt-1">
                  Universidad Interactiva y a Distancia del Estado de Guanajuato - UNIDEG
                </p>
              </div>
            </section>

            {/* Languages */}
            <section>
              <SectionTitle>Idiomas</SectionTitle>
              <div style={{ color: COLORS.muted }} className="text-sm space-y-2">
                <p><span style={{ color: COLORS.ink }} className="font-medium">Español:</span> Nativo</p>
                <p>
                  <span style={{ color: COLORS.ink }} className="font-medium">Inglés:</span> Comprensión de lectura
                  técnica y documentación. Nivel intermedio en lectura y comprensión escrita.
                </p>
              </div>
            </section>

            {/* Tech Stack */}
            <section>
              <SectionTitle>Stack de herramientas</SectionTitle>
              <div className="space-y-3 text-sm">
                <div>
                  <p style={{ color: COLORS.ink }} className="font-medium">Frontend:</p>
                  <p style={{ color: COLORS.muted }}>JavaScript, React, Next, Angular, jQuery, HTML, Tailwind, Bootstrap, CSS</p>
                </div>
                <div>
                  <p style={{ color: COLORS.ink }} className="font-medium">Backend:</p>
                  <p style={{ color: COLORS.muted }}>PHP, Laravel, Yii2, MySQL, .NET, C#, Node, Postgres, Python</p>
                </div>
                <div>
                  <p style={{ color: COLORS.ink }} className="font-medium">Otras herramientas:</p>
                  <p style={{ color: COLORS.muted }}>Vendor, WordPress, Git, GitHub APIs, AJAX, Linux, XAMPP, Agile, MVC</p>
                </div>
              </div>
            </section>

            {/* Interests */}
            <section>
              <SectionTitle>Intereses</SectionTitle>
              <p style={{ color: COLORS.muted }} className="text-sm leading-relaxed">
                Me interesa la exploración tecnológica orientada al procesamiento y visualización de datos para
                respaldar la toma de decisiones. Disfruto estructurar e integrar información que mejore el control
                de procesos en sistemas complejos, especialmente en entornos industriales. También me motiva aprender
                nuevas tecnologías que optimicen el rendimiento, reduzcan redundancias y permitan generar
                visualizaciones claras a partir de datos dinámicos.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CvWeb;